import { useEffect, useRef, useState } from 'react';
import { EditableContent } from '../../../edit/EditableContent';
import type { BlockRenderProps } from '../../../types';
import { isMenuItemActive } from '../menuEditHelpers';
import {
  expandMarqueeChips,
  readHighlightMenuSection,
  type MarqueeChipRef,
} from './highlightMenuItems';

/** ~chip width + gap — used to size how many repeats fill the viewport. */
const CHIP_SLOT_PX = 220;
const MIN_MARQUEE_CHIPS = 16;

function MarqueeGroup({
  chips,
  editable,
  target,
  ariaHidden,
}: {
  chips: MarqueeChipRef[];
  editable?: boolean;
  target: BlockRenderProps['activeEditTarget'];
  ariaHidden?: boolean;
}) {
  return (
    <ul className="ws-hl-marquee__group" aria-hidden={ariaHidden ? true : undefined}>
      {chips.map(({ item, columnIndex, itemIndex, cycle }) => {
        const isPrimary = !ariaHidden && cycle === 0;
        const itemActive =
          isPrimary &&
          isMenuItemActive(
            target?.kind === 'menu-item' ? target : null,
            columnIndex,
            itemIndex,
          );
        return (
          <li
            key={`${ariaHidden ? 'dup' : 'live'}-${cycle}-${columnIndex}-${itemIndex}`}
            className={['ws-hl-marquee__chip', itemActive ? 'ws-edit-child--active' : '']
              .filter(Boolean)
              .join(' ')}
            data-cycle={cycle}
            {...(editable && isPrimary
              ? {
                  'data-edit-child': 'menu-item' as const,
                  'data-column-index': columnIndex,
                  'data-item-index': itemIndex,
                }
              : {})}
          >
            <span className="ws-hl-marquee__name">{item.name}</span>
            {item.description ? (
              <span className="ws-hl-marquee__desc">{item.description}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** Continuous name·story marquee — light after-hero band (no dietary icons). */
export function MenuHighlightMarquee({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const { sectionTitle, sectionSubtitle, dishes } = readHighlightMenuSection(
    block,
    locale,
  );
  const target = isActive ? activeEditTarget : null;
  const viewportRef = useRef<HTMLDivElement>(null);
  const [minChips, setMinChips] = useState(MIN_MARQUEE_CHIPS);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const update = () => {
      const needed = Math.ceil(el.clientWidth / CHIP_SLOT_PX) + 2;
      setMinChips(Math.max(MIN_MARQUEE_CHIPS, needed));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [dishes.length]);

  const chips = expandMarqueeChips(dishes, minChips);

  return (
    <section
      className={['ws-hl-marquee', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-hl-marquee__inner">
        <header className="ws-hl-marquee__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-hl-marquee__title"
          >
            {sectionTitle}
          </EditableContent>
          {sectionSubtitle || editable ? (
            <EditableContent
              as="p"
              fieldKey="sectionSubtitle"
              editable={editable}
              activeEditTarget={target}
              className="ws-hl-marquee__subtitle"
            >
              {sectionSubtitle}
            </EditableContent>
          ) : null}
        </header>
        {dishes.length > 0 ? (
          <div className="ws-hl-marquee__viewport" ref={viewportRef}>
            <div className="ws-hl-marquee__track" aria-label={sectionTitle || 'Highlights'}>
              <MarqueeGroup chips={chips} editable={editable} target={target} />
              <MarqueeGroup chips={chips} editable={false} target={null} ariaHidden />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
